import { useCallback, useMemo } from "react";

import { generateId } from "~/utils/helpers";

import { Button } from "../button/Button";
import { Icon } from "../icon/Icon";
import { Text } from "../text/Text";
import { Truncate } from "../truncate/Truncate";

import "./Toast.css";

import type { ReactNode, CSSProperties } from "react";
import type { IntentProp } from "../components";
import type { IconProps } from "../icon/Icon";

export interface ToastProps {
  description?: ReactNode;
  dismissable?: boolean;
  icon?: IconProps["icon"];
  iconVariant?: IconProps["variant"];
  id?: string;
  intent?: Extract<IntentProp, "success" | "error" | "warning" | "info">;
  title?: ReactNode;
  onDismiss?: (id: string) => void;
  renderButton?: () => ReactNode;
}

export function Toast({
  description,
  icon,
  iconVariant,
  id = generateId(),
  intent = "info",
  dismissable = true,
  title,
  onDismiss,
  renderButton,
}: ToastProps) {
  const customProperties = {
    "--toast-color": `var(--color-${intent})`,
  } as CSSProperties;

  const handleDismissClick = useCallback(() => {
    onDismiss?.(id);
  }, [id, onDismiss]);

  const renderedTitle = useMemo(() => {
    if (typeof title === "string") {
      return <Truncate lines={1}>{title}</Truncate>;
    }

    return title;
  }, [title]);

  const renderActionButton = useCallback(() => {
    if (renderButton) return renderButton?.();

    if (dismissable) {
      return (
        <Button
          appearance="minimal"
          invert
          icon="xmark"
          shape="circle"
          size="xs"
          onClick={handleDismissClick}
        />
      );
    }
  }, [dismissable, handleDismissClick, renderButton]);

  return (
    <div className="Toast" style={customProperties}>
      {icon && <Icon icon={icon} variant={iconVariant} />}

      <div className="Toast__message">
        <Text weight="semibold">{renderedTitle}</Text>
        <Text size="sm">{description}</Text>
      </div>

      {renderActionButton()}
    </div>
  );
}
